import React, { useState, useEffect } from 'react';
import { User } from '@/services/userService';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { userService } from '@/lib/api/userService';
import { checkPasswordPolicy, getPasswordPolicyErrorMessage, PASSWORD_MIN_LENGTH } from '@/utils/passwordPolicy';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (user: Partial<User>) => Promise<void>;
  initialUser?: Partial<User>;
  availableAcls?: string[];
}

export const UserFormModal: React.FC<UserFormModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialUser,
  availableAcls = []
}) => {
  const { isFieldAllowed } = useAuth();
  const [formData, setFormData] = useState<Partial<User>>({});
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const canName = isFieldAllowed('name');
  const canUserName = isFieldAllowed('userName');
  const canCorreo = isFieldAllowed('correo');
  const canPassword = isFieldAllowed('password');
  const canPuesto = isFieldAllowed('puesto');
  const canRegion = isFieldAllowed('region');
  const canUserEstatus = isFieldAllowed('userEstatus');
  const canTipoUsuario = isFieldAllowed('tipoUsuario');
  const canAcl = isFieldAllowed('acl');

  useEffect(() => {
    if (!isOpen) { setIsDirty(false); return; }
    if (isOpen) {
      setIsDirty(false);
      setFormData({
        name: '',
        userName: '',
        correo: '',
        acl: '',
        puesto: '',
        region: '',
        userEstatus: 'Activo',
        tipoUsuario: 'Interno',
        password: '',
        ...(initialUser ?? {}),
      });
      setErrors({});
      setSubmitError(null);
      setShowResetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
      setResetError(null);
      setResetSuccess(false);
    }
  }, [isOpen, initialUser]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (canName && (!formData.name || formData.name.trim().length < 2)) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres';
    }

    if (canUserName && (!formData.userName || formData.userName.trim().length < 3)) {
      newErrors.userName = 'El nombre de usuario debe tener al menos 3 caracteres';
    }

    if (canCorreo) {
      if (!formData.correo) {
        newErrors.correo = 'El correo electrónico es requerido';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.correo)) {
          newErrors.correo = 'El correo electrónico no es válido';
        }
      }
    }

    if (canAcl && !formData.acl) {
      newErrors.acl = 'El rol (ACL) es requerido';
    }

    if (!initialUser?.ownerID && canPassword) {
      const check = checkPasswordPolicy(formData.password || '');
      if (!check.isValid) {
        newErrors.password =
          getPasswordPolicyErrorMessage(check) || 'La contraseña no cumple los requisitos';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (canAcl && (!formData.acl || formData.acl.trim() === '')) {
      setErrors(prev => ({ ...prev, acl: 'El rol (ACL) es requerido' }));
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError(null);
    try {
      // Asegurar que todos los campos requeridos están presentes
      const userDataToSubmit = {
        ...formData,
      };
      await onSubmit(userDataToSubmit);
      setIsDirty(false);
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || 'Error al guardar el usuario';
      setSubmitError(errorMsg);
      console.error('Error al guardar usuario:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof User, value: string) => {
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleResetPassword = async () => {
    setResetError(null);
    const check = checkPasswordPolicy(newPassword || '');
    if (!check.isValid) {
      setResetError(getPasswordPolicyErrorMessage(check) || 'La contraseña no cumple los requisitos');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Las contraseñas no coinciden');
      return;
    }
    if (!initialUser?.ownerID) return;
    setResetLoading(true);
    try {
      const resp = await userService.resetPassword(initialUser.ownerID, newPassword);
      if (resp.data) {
        setResetSuccess(true);
        setNewPassword('');
        setConfirmPassword('');
        setShowResetPassword(false);
        setTimeout(() => setResetSuccess(false), 4000);
      } else {
        setResetError(resp.error || 'Error al restablecer la contraseña');
      }
    } catch {
      setResetError('Error al restablecer la contraseña');
    } finally {
      setResetLoading(false);
    }
  };

  const isEditMode = !!initialUser?.ownerID;
  const passwordPolicy = checkPasswordPolicy(formData.password || '');
  const resetPasswordPolicy = checkPasswordPolicy(newPassword || '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      
      title={isEditMode ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
    >
      {submitError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{submitError}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {canName && (
          <div className="col-span-2">
            <Input 
              label="Nombre Completo" 
              value={formData.name || ''} 
              onChange={e => handleChange('name', e.target.value)} 
              required
              error={errors.name}
            />
          </div>
          )}
          
          {canUserName && (
          <Input 
            label="Nombre de Usuario (Login)" 
            value={formData.userName || ''} 
            onChange={e => handleChange('userName', e.target.value)} 
            required
            disabled={isEditMode} // No permitir cambiar el userName al editar
            error={errors.userName}
          />
          )}

          {canCorreo && (
          <Input 
            label="Correo Electrónico" 
            type="email" 
            value={formData.correo || ''} 
            onChange={e => handleChange('correo', e.target.value)} 
            required
            error={errors.correo}
          />
          )}

          {canAcl && (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol (ACL) <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm ${
                errors.acl ? 'border-red-300' : 'border-gray-300'
              }`}
              value={formData.acl || ''}
              onChange={e => handleChange('acl', e.target.value)}
              required
            >
              <option value="">Seleccionar rol...</option>
              {availableAcls.length > 0 ? (
                availableAcls.map(acl => (
                  <option key={acl} value={acl}>{acl}</option>
                ))
              ) : (
                <>
                  <option value="sysAdmin">SysAdmin</option>
                  <option value="all">Director (All)</option>
                  <option value="all_limited">All Limited</option>
                  <option value="oficialDeCumplimiento">Oficial de Cumplimiento</option>
                  <option value="gerenteComercial">Gerente Comercial</option>
                  <option value="gerenteCartera">Gerente Cartera</option>
                  <option value="gerenteRiesgos">Gerente Riesgos</option>
                  <option value="asesor">Asesor</option>
                  <option value="analistaRiesgos">Analista Riesgos</option>
                  <option value="gestorCobranza">Gestor Cobranza</option>
                  <option value="supervisorContable">Supervisor Contable</option>
                  <option value="analistaCumplimiento">Analista Cumplimiento</option>
                </>
              )}
            </select>
            {errors.acl && (
              <p className="mt-1 text-sm text-red-600">{errors.acl}</p>
            )}
          </div>
          )}

          {canUserEstatus && (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm"
              value={formData.userEstatus || 'Activo'}
              onChange={e => handleChange('userEstatus', e.target.value)}
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
              <option value="Baja">Baja</option>
            </select>
          </div>
          )}

          {canPuesto && (
          <Input 
            label="Puesto" 
            value={formData.puesto || ''} 
            onChange={e => handleChange('puesto', e.target.value)} 
          />
          )}

          {canRegion && (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">Región</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm"
              value={formData.region || ''}
              onChange={e => handleChange('region', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="CDMX">CDMX</option>
              <option value="Guadalajara">Guadalajara</option>
            </select>
          </div>
          )}

          {canTipoUsuario && (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Usuario</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196A1] focus:border-[#0196A1] bg-white shadow-sm"
              value={formData.tipoUsuario || 'Interno'}
              onChange={e => handleChange('tipoUsuario', e.target.value)}
            >
              <option value="Interno">Interno</option>
              <option value="Externo">Externo</option>
            </select>
          </div>
          )}
          
          {!isEditMode && canPassword && (
            <div className="col-span-2">
              <Input
                label="Contraseña Inicial"
                type="password"
                value={formData.password || ''}
                onChange={e => handleChange('password', e.target.value)}
                required
                error={errors.password}
              />
              {/* Indicadores de requisitos de contraseña */}
              <div className="mt-2 space-y-1">
                <p className="text-xs text-gray-500 mb-1">Requisitos de la contraseña:</p>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${passwordPolicy.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordPolicy.minLength ? '✓' : '○'}
                  </span>
                  <span className={`text-xs ${passwordPolicy.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                    Mínimo {PASSWORD_MIN_LENGTH} caracteres
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${passwordPolicy.hasLetter ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordPolicy.hasLetter ? '✓' : '○'}
                  </span>
                  <span className={`text-xs ${passwordPolicy.hasLetter ? 'text-green-600' : 'text-gray-500'}`}>
                    Al menos una letra
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${passwordPolicy.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordPolicy.hasNumber ? '✓' : '○'}
                  </span>
                  <span className={`text-xs ${passwordPolicy.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                    Al menos un número
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sección Resetear Contraseña (solo en modo edición) */}
        {isEditMode && canPassword && (
          <div className="border border-amber-200 rounded-lg bg-amber-50 overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors"
              onClick={() => { setShowResetPassword(v => !v); setResetError(null); }}
            >
              <span>🔑 Restablecer Contraseña</span>
              <span className="text-xs">{showResetPassword ? '▲ Ocultar' : '▼ Expandir'}</span>
            </button>
            {showResetPassword && (
              <div className="px-4 pb-4 space-y-3 border-t border-amber-200">
                <p className="text-xs text-amber-700 mt-3">
                  Asigna una nueva contraseña al usuario. Esta acción no requiere la contraseña actual.
                </p>
                <Input
                  label="Nueva Contraseña"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder={`Mínimo ${PASSWORD_MIN_LENGTH} caracteres, letra y número`}
                />
                <Input
                  label="Confirmar Contraseña"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repite la nueva contraseña"
                />
                {resetError && (
                  <p className="text-sm text-red-600">{resetError}</p>
                )}
                {resetSuccess && (
                  <p className="text-sm text-green-700 font-medium">✓ Contraseña restablecida correctamente</p>
                )}
                <Button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetLoading || !resetPasswordPolicy.isValid || newPassword !== confirmPassword}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-sm"
                >
                  {resetLoading ? 'Restableciendo...' : 'Confirmar Nuevo Password'}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
          <Button 
            type="button" 
            variant="ghost" 
            onClick={onClose} 
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            disabled={loading || (!isEditMode && canPassword && !passwordPolicy.isValid)}
            className="bg-[#0196A1] hover:bg-[#017a85] text-white"
          >
            {loading ? 'Guardando...' : (isEditMode ? 'Actualizar Usuario' : 'Crear Usuario')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
